import { AxiosError, isAxiosError } from 'axios';

interface IEmailAlreadyInUseResponse {
  message?: string;
  error?: string;
}

const DEFAULT_MESSAGE = 'An account with this email already exists.';

/**
 * auth-service is not consistent about the status it gives a duplicate email:
 * the office signup POST answers 400, other paths answer 409 (GOTU-7434).
 *
 * 400 is also the ordinary validation status — a weak password or a malformed
 * email arrives the same way — so on a 400 the status cannot decide alone and
 * the BE copy has to match too. Without that guard every rejected password
 * would open the Account Exists dialog. A 409 is unambiguous and needs no
 * copy match.
 */
const DUPLICATE_EMAIL_MESSAGE = /already\s+(registered|exists|in use|taken)/i;

export class EmailAlreadyInUseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EmailAlreadyInUseError';
  }

  static fromAxiosError(error: unknown): EmailAlreadyInUseError | null {
    if (!isAxiosError(error)) {
      return null;
    }

    const axiosError = error as AxiosError<IEmailAlreadyInUseResponse>;
    const status = axiosError.response?.status;
    const message = axiosError.response?.data?.message || axiosError.response?.data?.error;

    if (status === 409) {
      return new EmailAlreadyInUseError(message || DEFAULT_MESSAGE);
    }

    if (status === 400 && message && DUPLICATE_EMAIL_MESSAGE.test(message)) {
      return new EmailAlreadyInUseError(message);
    }

    return null;
  }
}
